import React, { useEffect, useRef, useState } from 'react';
import { format } from 'date-fns';
import { jsPDF } from 'jspdf';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';

import { selectUsersForNames } from '../../../../store/userSelectors';
import LoadingSpinner from '../../../ui/LoadingSpinner';

import PDFContent from './PDFContent';

import pdf from '../../../../commonStyles/pdf.module.css';

const GenerateFinancialRequestPDF = ({
    financialRequest,
    participantSignature = '',
    financialRequestDownloaded = () => {}
}) => {
    const templateContainerRef = useRef(null);

    // LOCAL STATE
    const [isPDFRendering, setIsPDFRendering] = useState(false);

    // STORE STATE
    const loggedInUser = useSelector((state) => state.entities.userService.loggedInUser);
    const currentParticipant = useSelector(
        (state) => state.entities.participantService.currentParticipant
    );
    const users = useSelector(selectUsersForNames);

    // USEEFFECTS
    useEffect(() => {
        if (!currentParticipant?.id || !financialRequest?.id) return;
        setIsPDFRendering(true);
    }, [currentParticipant, financialRequest]);

    useEffect(() => {
        if (!isPDFRendering || !templateContainerRef.current) return;

        const doc = new jsPDF();
        doc.setFontSize(12);
        const fileName = `${format(new Date(), `dd-MM-yyyy_HH-mm`)} Participant Financial Request Detail`;
        doc.html(templateContainerRef.current, {
            async callback(doc) {
                createFooter(doc);
                doc.save(fileName);
                financialRequestDownloaded();
            },
            autoPaging: 'text',
            margin: [10, 0, 10, 0],
            width: 210,
            windowWidth: 1050
        });
    }, [isPDFRendering]);

    // HELPER FNS
    const createFooter = (doc) => {
        const pageCount = doc.internal.getNumberOfPages();
        const pageSize = doc.internal.pageSize;

        doc.setFontSize(8);

        Array.from({ length: pageCount }, (_, i) => i + 1).forEach((pageNumber) => {
            doc.setPage(pageNumber);
            const printedBy = users.find((el) => el.id === loggedInUser?.id)?.code || '-';
            const footerRight = `Printed by ${printedBy} on ${format(new Date(), 'EEEE, d MMMM yyyy, HH:mm')} Print ID: - PTID: ${currentParticipant.ptCode || '-'}`;
            const padding = Math.max(1, 175 - footerRight.length);
            const footerText =
                `    Page ${pageNumber} of ${pageCount} ` + ' '.repeat(padding) + footerRight;

            doc.text(footerText, 1, pageSize.height - 5, { align: 'left' });
        });
    };

    return (
        <>
            {!isPDFRendering ? (
                <LoadingSpinner />
            ) : (
                <div
                    id="contentTemplateContainer"
                    className={pdf.pdfWrapper}
                    ref={templateContainerRef}>
                    <PDFContent
                        authorisedById={financialRequest.approverId}
                        authoriserSignature={financialRequest.authoriserSignature}
                        comments={financialRequest.approverNote}
                        orderAmount={financialRequest.amount}
                        orderDate={
                            financialRequest.dateIssued
                                ? format(new Date(financialRequest.dateIssued), 'yyyy-MM-dd')
                                : format(new Date(), 'yyyy-MM-dd')
                        }
                        participant={currentParticipant}
                        participantSignature={participantSignature}
                        paymentMethod={financialRequest.paymentMethod || ''}
                        paymentMethodType={financialRequest.paymentMethodType || ''}
                        reason={financialRequest.reasonDetail}
                        requestedById={financialRequest.adviserId}
                    />
                </div>
            )}
        </>
    );
};

GenerateFinancialRequestPDF.propTypes = {
    financialRequest: PropTypes.object.isRequired,
    participantSignature: PropTypes.string,
    financialRequestDownloaded: PropTypes.func
};

export default GenerateFinancialRequestPDF;
